import { useState, useRef, useCallback, useEffect } from "react";
import { apiRequest } from "@/lib/api";
import {
  Languages, Mic, MicOff, Volume2, ArrowLeftRight, Copy, Check, Loader2, RotateCcw, Lightbulb, X
} from "lucide-react";
import "./Translator.css";

interface TranslationResult {
  translation: string;
  pronunciation: string;
  alternatives: string[];
  culturalNote: string;
}

const LANGUAGES = [
  { code: "Spanish", flag: "🇪🇸", label: "Spanish" },
  { code: "French", flag: "🇫🇷", label: "French" },
  { code: "Mandarin Chinese", flag: "🇨🇳", label: "Mandarin" },
  { code: "Vietnamese", flag: "🇻🇳", label: "Vietnamese" },
  { code: "Korean", flag: "🇰🇷", label: "Korean" },
  { code: "Arabic", flag: "🇸🇦", label: "Arabic" },
  { code: "Portuguese", flag: "🇧🇷", label: "Portuguese" },
  { code: "Japanese", flag: "🇯🇵", label: "Japanese" },
  { code: "German", flag: "🇩🇪", label: "German" },
  { code: "Hindi", flag: "🇮🇳", label: "Hindi" },
  { code: "Russian", flag: "🇷🇺", label: "Russian" },
  { code: "Italian", flag: "🇮🇹", label: "Italian" },
];

const QUICK_PHRASES = [
  { label: "Welcome", text: "Welcome to our store! How can I help you today?", context: "greeting" as const },
  { label: "Financing", text: "We offer 0% financing for up to 60 months on qualifying purchases.", context: "negotiation" as const },
  { label: "Delivery", text: "Free delivery is included. We can deliver as soon as this week.", context: "sales" as const },
  { label: "Materials", text: "This piece is made with solid hardwood and top-grain leather. Very durable.", context: "technical" as const },
  { label: "Best price", text: "Let me check if I can get you the best price on this today.", context: "negotiation" as const },
  { label: "Color options", text: "This comes in several colors. Would you like to see fabric swatches?", context: "sales" as const },
  { label: "Warranty", text: "This comes with a 5-year manufacturer warranty covering defects.", context: "technical" as const },
  { label: "Sale today", text: "We have a special promotion today. You can save up to 40% off.", context: "negotiation" as const },
  { label: "Dimensions", text: "Let me get you the exact dimensions so you can make sure it fits.", context: "technical" as const },
  { label: "Thank you", text: "Thank you for visiting! Please come back anytime.", context: "greeting" as const },
];

type TranslateContext = "sales" | "greeting" | "technical" | "negotiation";

const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

export default function Translator() {
  const [sourceText, setSourceText] = useState("");
  const [sourceLang, setSourceLang] = useState("English");
  const [targetLang, setTargetLang] = useState("Spanish");
  const [context, setContext] = useState<TranslateContext>("sales");
  const [result, setResult] = useState<TranslationResult | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [copied, setCopied] = useState<"source" | "translation" | null>(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState<Array<{ source: string; translation: string; from: string; to: string }>>([]);
  const recognitionRef = useRef<any>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const swapLanguages = () => {
    const temp = sourceLang;
    setSourceLang(targetLang);
    setTargetLang(temp);
    if (result) {
      setSourceText(result.translation);
      setResult(null);
    }
  };

  const translate = useCallback(async (text?: string, ctx?: TranslateContext) => {
    const inputText = text || sourceText;
    if (!inputText.trim()) return;
    setIsTranslating(true);
    setError("");
    setResult(null);
    try {
      const data = await apiRequest<TranslationResult>("/translate", {
        method: "POST",
        body: JSON.stringify({
          text: inputText,
          sourceLang,
          targetLang,
          context: ctx || context,
        }),
      });
      setResult(data);
      setHistory((prev) => [
        { source: inputText, translation: data.translation, from: sourceLang, to: targetLang },
        ...prev.slice(0, 9),
      ]);
    } catch (err: any) {
      setError(err.message);
    }
    setIsTranslating(false);
  }, [sourceText, sourceLang, targetLang, context]);

  const handleQuickPhrase = (phrase: typeof QUICK_PHRASES[0]) => {
    setSourceText(phrase.text);
    setContext(phrase.context);
    translate(phrase.text, phrase.context);
  };

  const startListening = () => {
    if (!SpeechRecognition) {
      setError("Speech recognition is not supported in this browser. Please use Chrome or Edge.");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = getRecognitionLang(sourceLang);

    recognition.onresult = (event: any) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setSourceText(transcript);
    };

    recognition.onerror = (event: any) => {
      console.error("Speech error:", event.error);
      if (event.error !== "aborted") {
        setError(`Speech recognition error: ${event.error}`);
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
    setError("");
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
    if (sourceText.trim()) {
      translate();
    }
  };

  const speakText = (text: string, lang: string) => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = getSpeechLang(lang);
    utterance.rate = 0.9;
    window.speechSynthesis.speak(utterance);
  };

  const copyText = async (text: string, type: "source" | "translation") => {
    await navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const clearAll = () => {
    setSourceText("");
    setResult(null);
    setError("");
  };

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Language Translator</h1>
        <p>Real-time translation for customer conversations on the showroom floor</p>
      </div>

      <div className="translator-controls">
        <div className="lang-selector">
          <label>From</label>
          <div className="lang-chips-scroll">
            <button
              className={`lang-chip ${sourceLang === "English" ? "active" : ""}`}
              onClick={() => setSourceLang("English")}>
              🇺🇸 English
            </button>
            {LANGUAGES.map((l) => (
              <button key={l.code} className={`lang-chip ${sourceLang === l.code ? "active" : ""}`}
                onClick={() => setSourceLang(l.code)}>
                {l.flag} {l.label}
              </button>
            ))}
          </div>
        </div>

        <button className="swap-btn" onClick={swapLanguages} title="Swap languages">
          <ArrowLeftRight size={18} />
        </button>

        <div className="lang-selector">
          <label>To</label>
          <div className="lang-chips-scroll">
            {LANGUAGES.map((l) => (
              <button key={l.code} className={`lang-chip ${targetLang === l.code ? "active" : ""}`}
                onClick={() => setTargetLang(l.code)}>
                {l.flag} {l.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="context-selector">
        <Lightbulb size={14} />
        <span>Context:</span>
        {(["sales", "greeting", "technical", "negotiation"] as TranslateContext[]).map((c) => (
          <button key={c} className={`context-chip ${context === c ? "active" : ""}`}
            onClick={() => setContext(c)}>
            {c.charAt(0).toUpperCase() + c.slice(1)}
          </button>
        ))}
      </div>

      <div className="translator-main">
        <div className="card translate-panel source-panel">
          <div className="panel-header">
            <span className="panel-label">{sourceLang}</span>
            <div className="panel-actions">
              {sourceText && (
                <button className="icon-btn" onClick={clearAll} title="Clear"><X size={16} /></button>
              )}
              <button className="icon-btn" onClick={() => copyText(sourceText, "source")} title="Copy" disabled={!sourceText}>
                {copied === "source" ? <Check size={16} /> : <Copy size={16} />}
              </button>
              <button className="icon-btn" onClick={() => speakText(sourceText, sourceLang)} title="Listen" disabled={!sourceText}>
                <Volume2 size={16} />
              </button>
            </div>
          </div>
          <textarea
            ref={textareaRef}
            className="translate-textarea"
            value={sourceText}
            onChange={(e) => setSourceText(e.target.value)}
            placeholder={isListening ? "Listening... speak now" : "Type or speak to translate..."}
            rows={5}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); translate(); } }}
          />
          <div className="panel-footer">
            <button
              className={`mic-btn ${isListening ? "listening" : ""}`}
              onClick={isListening ? stopListening : startListening}>
              {isListening ? <><MicOff size={18} /> Stop</> : <><Mic size={18} /> Speak</>}
            </button>
            <button className="btn btn-primary" onClick={() => translate()} disabled={isTranslating || !sourceText.trim()}>
              {isTranslating ? <><Loader2 size={16} className="spinning" /> Translating...</> : <><Languages size={16} /> Translate</>}
            </button>
          </div>
        </div>

        <div className="card translate-panel result-panel">
          <div className="panel-header">
            <span className="panel-label">{targetLang}</span>
            <div className="panel-actions">
              <button className="icon-btn" onClick={() => copyText(result?.translation || "", "translation")}
                title="Copy" disabled={!result}>
                {copied === "translation" ? <Check size={16} /> : <Copy size={16} />}
              </button>
              <button className="icon-btn" onClick={() => speakText(result?.translation || "", targetLang)}
                title="Listen" disabled={!result}>
                <Volume2 size={16} />
              </button>
            </div>
          </div>
          <div className="translate-result">
            {isTranslating ? (
              <div className="translating-placeholder">
                <Loader2 size={24} className="spinning" />
                <span>Translating...</span>
              </div>
            ) : result ? (
              <>
                <div className="translation-text">{result.translation}</div>
                {result.pronunciation && (
                  <div className="pronunciation">
                    <span className="pronunciation-label">Pronunciation:</span> {result.pronunciation}
                  </div>
                )}
                {result.alternatives && result.alternatives.length > 0 && (
                  <div className="alternatives">
                    <span className="alternatives-label">Alternatives:</span>
                    {result.alternatives.map((alt, i) => (
                      <span key={i} className="alt-chip" onClick={() => speakText(alt, targetLang)}>{alt}</span>
                    ))}
                  </div>
                )}
                {result.culturalNote && (
                  <div className="cultural-note">
                    <Lightbulb size={14} /> {result.culturalNote}
                  </div>
                )}
              </>
            ) : (
              <div className="empty-result">Translation will appear here</div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="login-error" style={{ marginTop: 12 }}>{error}</div>}

      <div className="card quick-phrases-card">
        <h3>Quick Phrases</h3>
        <p className="quick-phrases-subtitle">Common furniture sales phrases — tap to translate instantly</p>
        <div className="quick-phrases-grid">
          {QUICK_PHRASES.map((phrase, i) => (
            <button key={i} className="quick-phrase-btn" onClick={() => handleQuickPhrase(phrase)}
              title={phrase.text}>
              <span className="phrase-label">{phrase.label}</span>
              <span className="phrase-preview">{phrase.text.slice(0, 50)}{phrase.text.length > 50 ? "..." : ""}</span>
            </button>
          ))}
        </div>
      </div>

      {history.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header-row">
            <h3>Recent Translations</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => setHistory([])}>
              <RotateCcw size={14} /> Clear
            </button>
          </div>
          <div className="history-list">
            {history.map((h, i) => (
              <div key={i} className="history-item" onClick={() => { setSourceText(h.source); setResult({ translation: h.translation, pronunciation: "", alternatives: [], culturalNote: "" }); }}>
                <div className="history-source">{h.source}</div>
                <div className="history-arrow">→</div>
                <div className="history-translation">{h.translation}</div>
                <div className="history-langs">{h.from} → {h.to}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function getRecognitionLang(lang: string): string {
  const map: Record<string, string> = {
    "English": "en-US",
    "Spanish": "es-ES",
    "French": "fr-FR",
    "Mandarin Chinese": "zh-CN",
    "Vietnamese": "vi-VN",
    "Korean": "ko-KR",
    "Arabic": "ar-SA",
    "Portuguese": "pt-BR",
    "Japanese": "ja-JP",
    "German": "de-DE",
    "Hindi": "hi-IN",
    "Russian": "ru-RU",
    "Italian": "it-IT",
  };
  return map[lang] || "en-US";
}

function getSpeechLang(lang: string): string {
  return getRecognitionLang(lang);
}
